/**
 * Templates Service
 * 
 * Handles all template-related API calls.
 * Templates group apps together for easier user assignment.
 * 
 * @module services/templates
 */

import { apiClient, ApiResponse } from './api';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Template app assignment
 */
export interface TemplateApp {
  id?: number;
  appId: number;
  roleId?: number | null;
  appName?: string;
  appIcon?: string;
  appColor?: string;
  roleName?: string;
}

/**
 * Template entity
 */
export interface Template {
  id: number;
  templateName: string;
  templateDescription: string | null;
  templateColor: string;
  templateIcon: string;
  isActive: boolean;
  /** Departments / sub-departments this template is auto-assigned for */
  departmentIds?: number[];
  departmentNames?: string[];
  createdAt: string;
  updatedAt: string;
  templateApps?: TemplateApp[];
}

/**
 * DTO for creating a template
 */
export interface CreateTemplateDto {
  templateName: string;
  templateDescription?: string;
  templateColor?: string;
  templateIcon?: string;
  isActive?: boolean;
  departmentIds?: number[];
  apps?: { appId: number; roleId?: number }[];
}

/**
 * DTO for updating a template
 */
export interface UpdateTemplateDto extends Partial<CreateTemplateDto> { }

// =============================================================================
// TEMPLATES SERVICE
// =============================================================================

export const templatesService = {
  // ===========================================================================
  // READ OPERATIONS
  // ===========================================================================

  /**
   * Get all templates
   */
  getAll: async (): Promise<Template[]> => {
    const response = await apiClient.get<ApiResponse<Template[]>>('/templates');
    return response.data.data;
  },

  /**
   * Get all active templates
   */
  getActive: async (): Promise<Template[]> => {
    const response = await apiClient.get<ApiResponse<Template[]>>('/templates/active');
    return response.data.data;
  },

  /**
   * Get a template by ID
   */
  getById: async (id: number): Promise<Template> => {
    const response = await apiClient.get<ApiResponse<Template>>(`/templates/${id}`);
    return response.data.data;
  },

  /**
   * Get apps in a template with full details
   */
  getTemplateApps: async (id: number): Promise<TemplateApp[]> => {
    const response = await apiClient.get<ApiResponse<TemplateApp[]>>(`/templates/${id}/apps`);
    return response.data.data;
  },

  /**
   * Get count of users using this template
   */
  getTemplateUsersCount: async (id: number): Promise<number> => {
    const response = await apiClient.get<ApiResponse<{ count: number }>>(`/templates/${id}/users-count`);
    return response.data.data.count;
  },

  // ===========================================================================
  // WRITE OPERATIONS
  // ===========================================================================

  /**
   * Create a new template
   */
  create: async (data: CreateTemplateDto): Promise<Template> => {
    const response = await apiClient.post<ApiResponse<Template>>('/templates', data);
    return response.data.data;
  },

  /**
   * Update a template
   */
  update: async (id: number, data: UpdateTemplateDto): Promise<Template> => {
    const response = await apiClient.patch<ApiResponse<Template>>(`/templates/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a template
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/templates/${id}`);
  },

  /**
   * Toggle template active status
   */
  toggleActive: async (id: number): Promise<Template> => {
    const response = await apiClient.patch<ApiResponse<Template>>(`/templates/${id}/toggle-active`);
    return response.data.data;
  },

  /**
   * Add an app to a template
   */
  addApp: async (templateId: number, appId: number, roleId?: number): Promise<Template> => {
    const response = await apiClient.post<ApiResponse<Template>>(`/templates/${templateId}/apps`, {
      appId,
      roleId,
    });
    return response.data.data;
  },

  /**
   * Remove an app from a template
   */
  removeApp: async (templateId: number, appId: number): Promise<Template> => {
    const response = await apiClient.delete<ApiResponse<Template>>(`/templates/${templateId}/apps/${appId}`);
    return response.data.data;
  },
};

export default templatesService;

